import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Download, PencilLine } from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";

import { APIError } from "../../../api/utils/errors";
import { sanitizeDownloadFilename } from "../../../helpers/downloadFilename";
import type {
  EditableFile,
  EditableFileStore,
  FileCaseContext
} from "../../caseFiles/editableFile";
import { fileKindForName } from "../../caseFiles/fileKind";
import {
  filePreviewKind,
  type FilePreviewKind
} from "../../caseFiles/filePreview";
import {
  VersionHistoryMenu,
  versionCountLabel
} from "../pdfEditor/VersionHistoryMenu";
import type { EditTarget } from "../pdfEditor/editTarget";
import type { SnippetAction } from "../pdfSnippet/toolbar";
import { ImagePreview } from "./ImagePreview";

// The viewer and editors pull in PDFium, Univer, the slides engine or the docx
// engine; only people who open the matching file pay for each.
const PdfViewer = lazy(() =>
  import("../pdfViewer/PdfViewer").then((m) => ({ default: m.PdfViewer }))
);
const PdfEditor = lazy(() =>
  import("../pdfEditor/PdfEditor").then((m) => ({ default: m.PdfEditor }))
);
const CaseFileEditorShell = lazy(() =>
  import("../../officeEditor/CaseFileEditorShell").then((m) => ({
    default: m.CaseFileEditorShell
  }))
);
const CaseFileDocxEditor = lazy(() =>
  import("../../officeEditor/CaseFileDocxEditor").then((m) => ({
    default: m.CaseFileDocxEditor
  }))
);

export type FileViewerProps = {
  /** The file and where it saves: a case file or a client document. */
  store: EditableFileStore;
  /** Formatted exhibit label ("Exhibit A") or empty. */
  label?: string;
  page?: number | null;
  /** Only for a case file in the Case IDE (the live agent, Make This a Draft). */
  caseContext?: FileCaseContext;
  /** Shown above the viewer (a case file's copy-source strip). */
  topSlot?: ReactNode;
  /** Bump to fetch the bytes again when the revision is unchanged. */
  reloadKey?: number;
  /** A new version was saved (the host refreshes its list). */
  onSaved?: (file: EditableFile) => void;
  /** An earlier version was restored (the host refreshes its list). */
  onRestored?: () => void;
  /** The Word editor's Close. */
  onClose?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** Text highlighted in the PDF viewer or the Word editor. */
  onTextSelectionChange?: (text: string | null) => void;
};

export const Notice: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
    {children}
  </div>
);

const Loading: FC<{ label: string }> = ({ label }) => (
  <div className="flex h-full items-center justify-center">
    <BrandLoader label={label} />
  </div>
);

/**
 * Which bytes the viewer shows for a file. Everything the app can draw itself
 * (PDFs, Office packages, images) uses the original upload; legacy Office
 * files and "annotate this image as a PDF" ask the server for a PDF, which it
 * converts the first time and caches. Nothing is converted at upload, so
 * there is never a preview to wait for.
 */
type BytesSource = "original" | "pdf" | null;

const bytesSourceFor = (
  file: EditableFile,
  kind: FilePreviewKind,
  editing: boolean
): BytesSource => {
  if (!file.hasBytes) return null;
  // The docx editor loads its own bytes.
  if (kind === "word") return null;
  if (kind === "legacy-office") return "pdf";
  if (kind === "image" && editing) return "pdf";
  if (kind === "external") return null;
  return "original";
};

const save = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = sanitizeDownloadFilename(name);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/** Reads the blob once and hands the editor an ArrayBuffer. */
const OfficeBytes: FC<{
  file: Blob;
  children: (bytes: ArrayBuffer) => ReactNode;
}> = ({ file, children }) => {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  useEffect(() => {
    let cancelled = false;
    setBytes(null);
    file.arrayBuffer().then((buffer) => !cancelled && setBytes(buffer));
    return () => {
      cancelled = true;
    };
  }, [file]);
  if (!bytes) return <Loading label="Loading file" />;
  return <>{children(bytes)}</>;
};

/**
 * A file open in the Case IDE's viewers and editors, routed by what the file
 * is (see `filePreviewKind`, LegalWork's `classifyOpenTarget`):
 *
 * - PDF: the viewer; Edit switches to the PDF editor.
 * - Word (.docx): the in-app docx editor straight away, saving new versions.
 * - Sheet / slides (.xlsx, .pptx): the in-app Univer / slides editor on the
 *   original bytes, read-only to read and editable after Edit.
 * - Image: the image itself; "Annotate as PDF" asks the server for a PDF of
 *   it and edits that (the file becomes a PDF on save).
 * - Legacy Office (.doc, .xls, .ppt): a PDF the server converts on demand,
 *   with a download for editing elsewhere.
 *
 * Every save is a new version of the file, listed in the version menu. The
 * Case IDE opens case files through `FilePane`; client documents open here
 * from their preview.
 */
export const FileViewer: FC<FileViewerProps> = ({
  store,
  label = "",
  page,
  caseContext,
  topSlot,
  reloadKey: hostReloadKey = 0,
  onSaved,
  onRestored,
  onClose,
  onDirtyChange,
  onTextSelectionChange
}) => {
  const { file: current } = store;
  const [file, setFile] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirtyState] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // The page the reader is on, so Edit opens the editor there.
  const [viewerPage, setViewerPage] = useState<number | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  // Clicking the toolbar's Versions button while the menu is open dismisses
  // the menu on pointerdown; the click that follows must not reopen it.
  const versionsClosedAt = useRef(0);
  const setVersionsMenu = useCallback((next: boolean) => {
    if (!next) versionsClosedAt.current = Date.now();
    setVersionsOpen(next);
  }, []);
  const toggleVersions = useCallback(() => {
    if (Date.now() - versionsClosedAt.current < 300) return;
    setVersionsOpen((v) => !v);
  }, []);
  // Bumped after a save or restore so the bytes are fetched again even when
  // the revision is unchanged.
  const [reloadKey, setReloadKey] = useState(0);
  const kind = filePreviewKind({ name: current.filename });
  const source = bytesSourceFor(current, kind, editing);
  const fileId = current.id;
  const { revision } = current;
  const lastFileId = useRef(fileId);
  const storeRef = useRef(store);
  storeRef.current = store;
  const title = label ? `${current.title} (${label})` : current.title;

  const setDirty = useCallback(
    (next: boolean) => {
      setDirtyState(next);
      onDirtyChange?.(next);
    },
    [onDirtyChange]
  );

  useEffect(() => {
    let cancelled = false;
    setFile(null);
    setError(null);
    if (!source) return undefined;
    const fetchBytes =
      source === "pdf"
        ? storeRef.current.loadPdf()
        : storeRef.current.loadOriginal();
    fetchBytes
      .then((blob: Blob) => !cancelled && setFile(blob))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          source === "pdf" && err instanceof APIError && err.status === 415
            ? "This file can't be shown as a PDF."
            : source === "pdf"
            ? "Couldn't prepare a PDF of this file."
            : "Couldn't load this file."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [source, fileId, revision, reloadKey, hostReloadKey]);

  // Switching tabs swaps the file under us; the editor can't follow it.
  useEffect(() => {
    if (lastFileId.current === fileId) return;
    lastFileId.current = fileId;
    setViewerPage(null);
    if (editing) {
      if (dirty) toast.warning("Unsaved edits were discarded.");
      setEditing(false);
      setDirty(false);
    }
  }, [fileId, editing, dirty, setDirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const exitEditor = useCallback(() => {
    setEditing(false);
    setDirty(false);
    setConfirmDiscard(false);
  }, [setDirty]);

  const requestCancel = useCallback(() => {
    if (dirty) setConfirmDiscard(true);
    else exitEditor();
  }, [dirty, exitEditor]);

  const handlePdfSaved = useCallback(
    (updated: EditableFile, info: { pageStructureChanged: boolean }) => {
      exitEditor();
      setReloadKey((k) => k + 1);
      onSaved?.(updated);
      toast.success(`Saved a new version of ${updated.filename}`, {
        description: info.pageStructureChanged
          ? "Pages were added, removed or reordered, so page citations to this exhibit in drafts may now point elsewhere."
          : undefined,
        duration: info.pageStructureChanged ? 8000 : undefined
      });
    },
    [exitEditor, onSaved]
  );

  // Office saves keep the editor open; the host just needs the new version.
  const handleOfficeSaved = useCallback(
    (updated: EditableFile) => {
      onSaved?.(updated);
      toast.success(`Saved a new version of ${updated.filename}`);
    },
    [onSaved]
  );

  const handleRestored = useCallback(() => {
    setReloadKey((k) => k + 1);
    onRestored?.();
  }, [onRestored]);

  const editTarget = useMemo<EditTarget>(
    () => ({ kind: "file", store }),
    [store]
  );

  const download = useCallback(() => {
    const target = storeRef.current;
    if (!target.file.hasBytes) return;
    target
      .loadOriginal()
      .then((blob: Blob) => save(blob, target.file.filename))
      .catch(() => toast.error("Couldn't download this file."));
  }, []);

  // Word files open in the editor itself; there is no separate reading view
  // (LegalWork does the same).
  if (kind === "word" && current.hasBytes) {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        data-testid="file-pane-word"
      >
        {topSlot}
        <div className="min-h-0 flex-1">
          <Suspense fallback={<Loading label="Loading the Word editor" />}>
            <CaseFileDocxEditor
              key={`${current.id}:${reloadKey}:${hostReloadKey}`}
              store={store}
              caseContext={caseContext}
              onSaved={handleOfficeSaved}
              onRestored={handleRestored}
              onClose={onClose}
              onDirtyChange={setDirty}
              onSelectionChange={(selection) =>
                onTextSelectionChange?.(selection?.text ?? null)
              }
            />
          </Suspense>
        </div>
      </div>
    );
  }

  if (!source) {
    return (
      <Notice>
        <span>
          {
            fileKindForName(current.filename, current.mimeType ?? undefined)
              .label
          }{" "}
          files have no preview here.
          {current.hasBytes ? (
            <>
              {" "}
              <Button variant="link" className="h-auto p-0" onClick={download}>
                Download {current.filename}
              </Button>
            </>
          ) : null}
        </span>
      </Notice>
    );
  }
  if (error) {
    return (
      <Notice>
        <span>
          {error}{" "}
          <Button variant="link" className="h-auto p-0" onClick={download}>
            Download {current.filename}
          </Button>
        </span>
      </Notice>
    );
  }
  if (!file) {
    return (
      <Loading label={source === "pdf" ? "Preparing a PDF" : "Loading file"} />
    );
  }

  // Sheets and slides open in their editor on the original bytes: read-only
  // to read, editable after Edit. There is no PDF in between.
  const officeFormat =
    kind === "sheet" ? "xlsx" : kind === "slides" ? "pptx" : null;
  if (officeFormat) {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        data-testid={editing ? "file-pane-editing" : "file-pane-office"}
      >
        <Suspense
          fallback={
            <Loading
              label={
                officeFormat === "xlsx"
                  ? "Loading the spreadsheet viewer"
                  : "Loading the slides viewer"
              }
            />
          }
        >
          <OfficeBytes file={file}>
            {(bytes) => (
              <CaseFileEditorShell
                key={`${current.id}:${current.checksum ?? ""}:${
                  editing ? "edit" : "read"
                }`}
                store={store}
                caseContext={caseContext}
                bytes={bytes}
                format={officeFormat}
                readOnly={!editing}
                onEdit={editing ? undefined : () => setEditing(true)}
                onSaved={handleOfficeSaved}
                onRestored={handleRestored}
                onClose={editing ? exitEditor : undefined}
                onDirtyChange={setDirty}
              />
            )}
          </OfficeBytes>
        </Suspense>
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        data-testid="file-pane-editing"
      >
        <Suspense fallback={<Loading label="Loading the PDF editor" />}>
          <PdfEditor
            key={`${current.id}:${current.checksum ?? ""}`}
            target={editTarget}
            file={file}
            initialPage={viewerPage ?? page ?? null}
            onSaved={handlePdfSaved}
            onCancel={requestCancel}
            onDirtyChange={setDirty}
          />
        </Suspense>
        <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
              <AlertDialogDescription>
                Nothing has been saved to {current.title}. Your edits will be
                lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction onClick={exitEditor}>
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (kind === "image") {
    return (
      <ImagePreview
        file={file}
        name={current.filename}
        ariaLabel={title}
        controlsEndSlot={
          <>
            <VersionHistoryMenu
              store={store}
              onRestored={handleRestored}
              iconOnly
            />
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Download"
              title="Download"
              onClick={download}
            >
              <Download />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Annotate as PDF"
              title="Annotate as PDF"
              onClick={() => setEditing(true)}
              data-testid="file-pane-edit"
            >
              <PencilLine />
            </Button>
          </>
        }
      />
    );
  }

  // The tab already names the file, so the version menu and Edit ride along
  // in the Snippet's own toolbar instead of a second header row.
  const versions = versionCountLabel(current);
  const actions: SnippetAction[] = [
    ...(kind !== "pdf"
      ? [
          {
            id: "download-original",
            label:
              kind === "legacy-office"
                ? "This is a PDF preview. Download the original to edit it."
                : `Download ${current.filename}`,
            icon: "cbFileDown",
            onSelect: download
          }
        ]
      : []),
    ...(versions
      ? [
          {
            id: "versions",
            label: `Version history (${versions})`,
            icon: "cbHistory",
            active: versionsOpen,
            onSelect: toggleVersions
          }
        ]
      : []),
    ...(kind === "pdf"
      ? [
          {
            id: "edit",
            label: "Edit PDF",
            icon: "pencilMarker",
            onSelect: () => setEditing(true)
          }
        ]
      : [])
  ];
  const viewer = (
    <div className="relative h-full min-h-0">
      <Suspense fallback={<Loading label="Loading the PDF viewer" />}>
        <PdfViewer
          file={file}
          ariaLabel={title}
          defaultZoomMode="page-width"
          scrollToPageIndex={page ? page - 1 : null}
          scrollToPageKey={page ?? null}
          onVisiblePageChange={setViewerPage}
          onTextSelectionChange={onTextSelectionChange}
          downloadName={
            kind === "pdf" ? current.filename : `${current.title}.pdf`
          }
          actions={actions}
        />
      </Suspense>
      {versions ? (
        // Drops down under the Snippet's Versions button, at the toolbar's
        // right edge.
        <div className="absolute right-4 top-11">
          <VersionHistoryMenu
            store={store}
            onRestored={handleRestored}
            open={versionsOpen}
            onOpenChange={setVersionsMenu}
            anchorOnly
          />
        </div>
      ) : null}
    </div>
  );
  if (!topSlot) return viewer;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {topSlot}
      <div className="min-h-0 flex-1">{viewer}</div>
    </div>
  );
};
